<!--
  @component Navbar.SearchOverlay

  A component that displays a search overlay for the navbar.
  It provides a container for search results or suggestions.

  @prop colorVariant - The color variant of the search overlay
  @prop css - Custom CSS styles to be applied to the search overlay

  Example:
  <Navbar.SearchOverlay colorVariant="primary">
    <h3>Search Results</h3>
    <ul>
      <li>Result 1</li>
      <li>Result 2</li>
      <li>Result 3</li>
    </ul>
  </Navbar.SearchOverlay>
-->

<script lang="ts">
  import { css } from '@agapi/ui/panda/css';
  import { searchStyles } from '../navbar.styles';
  import { getContext } from 'svelte';
  import { fly } from 'svelte/transition';
  import { dynamicColorStyles } from '@agapi/ui/panda/utils';
  import { cubicOut } from 'svelte/easing';
  import type { NavbarStore } from '../navbar-store.svelte';
  import type { NavbarSearchOverlayProps } from '../navbar.types';

  /** Props for the Navbar.SearchOverlay component. */
  const { children, css: cssProp, colorVariant = 'default' }: NavbarSearchOverlayProps = $props();

  /** Styles for the search overlay. */
  const styles = searchStyles.raw();
  /** Store for managing navbar state. */
  const navbarStore = getContext<NavbarStore>('navbar');

  /**
   * Handles click events on the search overlay.
   * @param {MouseEvent} e - The mouse event object.
   */
  const onpointerdown = (e: MouseEvent) => {
    navbarStore.triggerKeepSearchOpen();
  }

  /** Derived state indicating whether the search overlay is open. */
  const isOpen = $derived(navbarStore.isSearchOpen);
</script>

{#if isOpen}
  <div
    {onpointerdown}
    class={css(styles.overlay, cssProp)}
    transition:fly={{ duration: 200, y: -10, opacity: 0, easing: cubicOut }}
    aria-label="Search"
    data-type="search-overlay"
    style="
      background-color: {dynamicColorStyles(colorVariant).backgroundColor};
      color: {dynamicColorStyles(colorVariant).color};
    "
  >
    {#if children}
      {@render children()}
    {/if}
  </div>

{/if}
